import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type ChangeEvent,
} from 'react';
import { translate } from 'translations/i18n';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LD_COLLAB_REMOVE_PERMS_FLAG } from 'constants/featureFlags';
import { useIsCollaborating } from 'components/projects/collaboration/hooks';
import {
  ButtonCondensed,
  ButtonIconCondensed,
  Notice,
} from '@companycam/slab-web';

import {
  updateMetadata as _updateMetadata,
  convertActiveToken as _convertActiveToken,
  insertTokenStarter as _insertTokenStarter,
  transformTokens as _transformTokens,
} from './helpers/tokenutils';
import {
  splitGroupIntoUserTokens,
  removeWhitespace,
  isTokenExactMatch,
  tokenFilterMatch,
} from './helpers/mentionTokenHelpers';

import { TokenSuggestions, type TokenSuggestionsRef } from './TokenSuggestions';
import RecordableTypes from 'constants/recordableTypes';
import { type HotkeyCombos, Hotkeys } from 'components/hotkeys/Hotkeys';
import { type Metadata, type TokenDef } from './types';
import { CommentErrorBoundary } from './CommentErrorBoundary';
import { TOKEN_TYPE_GROUP } from 'javascripts/selectors/mentionTokens';

const StyledNotice = styled(Notice)`
  margin-bottom: var(--cc_size_spacing_s);

  .asset-gallery-content & {
    margin: var(--cc_size_spacing_s) var(--cc_size_spacing_l)
      calc(var(--cc_size_spacing_s) * -1);
  }
`;

const ALLOW_RESIZE_CHAR_LIMIT = 75;

export type TokenizedInputProps = {
  placeholder?: string;
  projectId?: string;
  tokenDefs?: TokenDef[];
  onSubmit?: (newtext: string, newtokens: Metadata[]) => boolean;
  submitDisabled?: boolean;
  recordableType:
    | (typeof RecordableTypes)['PHOTO']
    | (typeof RecordableTypes)['PROJECT']
    | (typeof RecordableTypes)['VIDEO'];
};

type State = {
  value: string;
  metadata: Metadata[];
  activeToken: Metadata | null;
  shouldUpdate: boolean;
};

const TokenizedInput = ({
  tokenDefs,
  submitDisabled,
  onSubmit,
  placeholder,
  projectId,
  recordableType,
}: TokenizedInputProps) => {
  const [state, setState] = useState<State>({
    value: '',
    metadata: [],
    activeToken: null,
    shouldUpdate: false,
  });

  const { value, metadata, activeToken, shouldUpdate } = state;
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<TokenSuggestionsRef>(null);
  const isCollaborating = useIsCollaborating(projectId);
  const isDisabled = submitDisabled || removeWhitespace(value).length === 0;

  const { [LD_COLLAB_REMOVE_PERMS_FLAG]: areCollabPermsHidden = true } =
    useFlags();

  // transformation: '@FirstLast' -> 'First Last' (actually just pulls from token defs by value)
  // returns { newtext, newtokens }
  const retokenizeForSubmit = useCallback(
    (_value: string, _metadata: Metadata[]) => {
      return _transformTokens(
        _value,
        _metadata,
        (text: string, __value: unknown) => {
          let newTokenText = text;
          const matchingDefs = tokenDefs.filter(
            (def) => def.value === __value && def.type !== TOKEN_TYPE_GROUP,
          );
          if (matchingDefs.length > 0) {
            newTokenText = matchingDefs[0].text.trim();
          }

          return newTokenText;
        },
      );
    },
    [tokenDefs],
  );

  // strips the 'TOKEN_STARTER' char from the front
  const getActiveTokenText = useCallback(
    (token: Metadata) => {
      return value.substring(token.start + 1, token.start + token.len);
    },
    [value],
  );

  // filters tokenDefs by active token text
  const filterTokens = useCallback(
    (token: Metadata) => {
      const filter = getActiveTokenText(token);
      return tokenDefs.filter((def) => tokenFilterMatch(def, filter));
    },
    [getActiveTokenText, tokenDefs],
  );

  // sets focus on input and sets specific cursor position
  const focusAtPosition = (cursorPosition: number) => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  };

  // convert the active token into a metadata token, and update focus/cursor
  const convertActiveToken = useCallback(
    (def: TokenDef, token: Metadata) => {
      if (def.type === TOKEN_TYPE_GROUP) {
        const { updatedValue, updatedMetadata } = splitGroupIntoUserTokens(
          def,
          value,
          metadata,
        );

        setState({
          value: updatedValue,
          metadata: updatedMetadata,
          activeToken: null,
          shouldUpdate: false,
        });

        focusAtPosition(updatedValue.length);

        return;
      }

      const {
        metadata: newMetadata,
        activeToken: newActiveToken,
        newtext,
        cursorPosition,
      } = _convertActiveToken(
        value,
        metadata,
        token,
        removeWhitespace(def.text),
        def.value,
        def.type,
      );

      setState({
        value: newtext,
        metadata: newMetadata,
        activeToken: newActiveToken,
        shouldUpdate: false,
      });
      focusAtPosition(cursorPosition);
    },
    [metadata, value],
  );

  // automatically deactivate or convert the active token when updating
  const autoUpdateActiveToken = useCallback(
    (token: Metadata) => {
      if (shouldUpdate) {
        setState({
          value,
          metadata,
          activeToken: token,
          shouldUpdate: false,
        });
        const filter = getActiveTokenText(token);
        const nameResults = filterTokens(token);

        // determine leading and trailing whitespace
        let filterEndsWithWhitespace = false;
        let filterBeginsWithWhitespace = false;

        if (filter.length > 0) {
          const filterFirstChar = filter[0];
          filterBeginsWithWhitespace =
            filterFirstChar !== filterFirstChar.trim();

          const filterLastChar = filter[filter.length - 1];
          filterEndsWithWhitespace = filterLastChar !== filterLastChar.trim();
        }

        // deactivate if filter begins with WS, or if no results and ends with WS
        const doCancelSuggestions =
          filterBeginsWithWhitespace ||
          (filterEndsWithWhitespace && nameResults.length === 0);

        if (doCancelSuggestions) {
          setState({ value, metadata, activeToken: null, shouldUpdate: false });
        }

        // convert automatically if exact (case-insensitive) match
        if (nameResults.length === 1) {
          const def = nameResults[0];
          if (isTokenExactMatch(def, filter)) {
            convertActiveToken(def, token);
          }
        }
      }
    },
    [
      convertActiveToken,
      filterTokens,
      getActiveTokenText,
      metadata,
      shouldUpdate,
      value,
    ],
  );

  const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    const { metadata: newMetadata, activeToken: newActiveToken } =
      _updateMetadata(value, newValue, metadata, activeToken);

    setState({
      value: newValue,
      metadata: newMetadata,
      activeToken: newActiveToken,
      shouldUpdate: true,
    });
  };

  const insertTokenStarter = () => {
    if (inputRef.current) {
      const selstart = inputRef.current.selectionStart;
      const selend = inputRef.current.selectionEnd;

      const {
        metadata: newMetadata,
        activeToken: newActiveToken,
        newtext,
        cursorPosition,
      } = _insertTokenStarter(value, metadata, [selstart, selend]);

      setState({
        value: newtext,
        metadata: newMetadata,
        activeToken: newActiveToken,
        shouldUpdate: false,
      });
      focusAtPosition(cursorPosition);
    }
  };

  const reset = () => {
    setState({
      value: '',
      metadata: [],
      activeToken: null,
      shouldUpdate: false,
    });
    inputRef.current?.focus();
  };

  const submitInput = useCallback(() => {
    if (isDisabled === false) {
      const { newtext, newtokens } = retokenizeForSubmit(value, metadata);
      const preserveText = onSubmit(newtext, newtokens);
      if (!preserveText) {
        reset();
      }
    }
  }, [isDisabled, metadata, onSubmit, retokenizeForSubmit, value]);

  useEffect(() => {
    if (state.activeToken && state.shouldUpdate) {
      autoUpdateActiveToken(state.activeToken);
    }
  }, [autoUpdateActiveToken, state]);

  const handleCancelSuggestions = useCallback(
    (keyboard?: boolean) => {
      if (keyboard !== true) {
        inputRef.current?.focus();
      }

      if (activeToken) {
        setState({ value, metadata, activeToken: null, shouldUpdate: false });
      }
    },
    [activeToken, metadata, value],
  );

  const hotkeys = useMemo((): HotkeyCombos => {
    return [
      // capture these so they don't navigate the media galleries regardless of modifier keys
      [
        ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'],
        () => {},
        {
          enabled: true,
          ignoreModifiers: true,
          allowDefault: !activeToken,
        },
      ],
      [
        ['Shift+Tab', 'Tab'],
        () => {
          suggestionsRef.current?.cancelSuggestions();
        },
        { enabled: true, allowDefault: true },
      ],
      [
        ['ArrowUp', 'ArrowLeft'],
        () => {
          suggestionsRef.current?.prevSuggestion();
        },
        {
          enabled: !!activeToken,
          allowDefault: !activeToken,
        },
      ],
      [
        ['ArrowDown', 'ArrowRight'],
        () => {
          suggestionsRef.current?.nextSuggestion();
        },
        {
          enabled: !!activeToken,
          allowDefault: !activeToken,
        },
      ],
      [
        'Shift+Enter',
        () => {}, // we want to allow propagation when suggestions are not active
        {
          enabled: !!activeToken,
          allowPropagation: !activeToken,
          allowDefault: !activeToken,
        },
      ],
      [
        'Enter',
        () => {
          if (activeToken) {
            suggestionsRef.current?.selectSuggestion();
          } else if (!activeToken) {
            submitInput();
          }
        },
        {
          enabled: true,
        },
      ],
      [
        'Escape',
        () => {
          if (activeToken) {
            suggestionsRef.current?.cancelSuggestions();
          } else {
            inputRef.current.blur();
          }
        },
        {
          enabled: true,
        },
      ],
    ];
  }, [activeToken, submitInput]);

  return (
    <CommentErrorBoundary onReset={reset}>
      <div className="comment-textarea-container">
        {isCollaborating ? (
          <StyledNotice size="small" iconName="domain">
            {areCollabPermsHidden
              ? translate('activity.tokenizedInput.alertMessage')
              : translate('activity.tokenizedInput.commentsSeenByCollabs')}
          </StyledNotice>
        ) : null}
        <div
          className={
            isCollaborating
              ? 'submit-container submit-container--withCollabSignage'
              : 'submit-container'
          }
        >
          <div className="input-container">
            <Hotkeys hotkeys={hotkeys}>
              <textarea
                className={
                  value.length > ALLOW_RESIZE_CHAR_LIMIT ? 'allow-resize' : ''
                }
                data-testid={
                  recordableType === RecordableTypes.PROJECT
                    ? 'activity__project-comments-textarea'
                    : 'activity__asset-comments-textarea'
                }
                ref={inputRef}
                placeholder={placeholder}
                spellCheck={false}
                rows={2}
                required
                value={value}
                onChange={onChange}
              />
            </Hotkeys>
            <ButtonCondensed
              data-testid={
                recordableType === RecordableTypes.PROJECT
                  ? 'activity__project-comments-post-button'
                  : 'activity__asset-comments-post-button'
              }
              color="primary"
              disabled={isDisabled}
              onClick={submitInput}
            >
              {translate('activity.tokenizedInput.post')}
            </ButtonCondensed>
            <div className="input-actions">
              <ButtonIconCondensed
                data-testid="activity__comment-mention-token-starter-button"
                color="primary"
                size="small"
                iconName="at"
                onClick={(e) => {
                  e?.preventDefault();
                  insertTokenStarter();
                }}
                className="hint--left"
                aria-label={translate(
                  'activity.tokenizedInput.mentionSomeoneAriaLabel',
                )}
                accessibilityLabel={translate(
                  'activity.tokenizedInput.mentionSomeoneAriaLabel',
                )}
              />
            </div>
          </div>
          {activeToken ? (
            <TokenSuggestions
              ref={suggestionsRef}
              tokenDefs={filterTokens(state.activeToken)}
              onSelect={(token) => convertActiveToken(token, state.activeToken)}
              onCancel={handleCancelSuggestions}
            />
          ) : null}
        </div>
      </div>
    </CommentErrorBoundary>
  );
};

export default TokenizedInput;
